import { useState, type CSSProperties } from 'react';
import { DiffVaderConfigSchema } from '@death-star/shared';
import { ApiError, apiJson, apiSendJson } from '../api-client.js';
import { useAuth } from '../auth.js';
import { useApiQuery } from '../use-api.js';
import type { DvMode, DvConfigShape } from './InheritanceBadge.js';

interface OrgReposResponse {
  repos: string[];
  cachedAt: string;
  stale?: boolean;
}

interface Props {
  org: string;
  existingRepo?: string;
  initial: DvConfigShape;
  onClose: () => void;
  onSaved: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'var(--loop-space-300)',
  zIndex: 200,
  overflowY: 'auto',
};

const dialogStyle: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  borderRadius: 'var(--loop-radius-md)',
  padding: 'var(--loop-space-300)',
  width: '100%',
  maxWidth: 460,
  marginTop: 'var(--loop-space-300)',
  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 528,
  color: 'var(--loop-neutral-900)',
  marginBottom: 'var(--loop-space-075)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--loop-space-100) var(--loop-space-150)',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  background: 'var(--loop-neutral-0)',
  color: 'var(--loop-neutral-1100)',
  boxSizing: 'border-box',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

export function RepoOverrideModal({ org, existingRepo, initial, onClose, onSaved }: Props) {
  const { idToken } = useAuth();
  const [repo, setRepo] = useState(existingRepo ?? '');
  const [mode, setMode] = useState<DvMode>(initial.mode);
  const [rerunOnPush, setRerunOnPush] = useState(initial.rerunOnPush);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const reposQuery = useApiQuery<OrgReposResponse>(
    ['org-repos', org],
    `/api/orgs/${encodeURIComponent(org)}/repos`
  );

  const listId = `repos-${org}`;

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await apiJson<OrgReposResponse>(
        `/api/orgs/${encodeURIComponent(org)}/repos?refresh=true`,
        idToken!
      );
      await reposQuery.refetch();
    } catch (err) {
      console.warn('Repo refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleSave() {
    if (!repo.trim()) {
      setError('Repo name is required.');
      return;
    }
    const parsed = DiffVaderConfigSchema.safeParse({ mode, rerunOnPush });
    if (!parsed.success) {
      setError(`Validation error: ${parsed.error.issues[0]?.message ?? 'invalid'}`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const endpoint = `/api/dv-config/repo/${encodeURIComponent(org)}/${encodeURIComponent(repo.trim())}`;
      await apiSendJson(endpoint, 'PUT', idToken!, { mode, rerunOnPush });
      onSaved();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.body);
      } else {
        setError((e as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }

  const reposData = reposQuery.data;

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dialogStyle}>
        <h3 style={{ margin: '0 0 var(--loop-space-200)', fontSize: '1rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>
          {existingRepo ? `Edit override: ${org}/${existingRepo}` : `Add repo override for ${org}`}
        </h3>

        <div style={{ marginBottom: 'var(--loop-space-150)' }}>
          <label style={labelStyle}>Repository</label>
          {existingRepo ? (
            <div style={{ ...inputStyle, background: 'var(--loop-neutral-100)', color: 'var(--loop-neutral-700)' }}>
              {existingRepo}
            </div>
          ) : (
            <>
              <input
                style={inputStyle}
                list={listId}
                value={repo}
                onChange={(e) => setRepo(e.target.value)}
                placeholder="repo-name"
                disabled={saving}
              />
              {reposData && (
                <datalist id={listId}>
                  {reposData.repos.map((r) => <option key={r} value={r} />)}
                </datalist>
              )}
              {reposData && (
                <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)', marginTop: 'var(--loop-space-050)', display: 'flex', alignItems: 'center', gap: 'var(--loop-space-075)' }}>
                  <em>Repo list cached {relativeTime(reposData.cachedAt)}.</em>
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', color: 'var(--loop-blue-600)', cursor: 'pointer', fontSize: '0.75rem', padding: 0 }}
                    onClick={() => { void handleRefresh(); }}
                    disabled={refreshing}
                  >
                    {refreshing ? 'Refreshing…' : '↻ Refresh from GitHub'}
                  </button>
                </div>
              )}
              {reposData?.stale && (
                <div style={{ fontSize: '0.75rem', color: '#d97706', marginTop: 'var(--loop-space-050)' }}>
                  Couldn&apos;t refresh from GitHub; showing cached list.
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ marginBottom: 'var(--loop-space-150)' }}>
          <label style={labelStyle}>Mode</label>
          <select
            style={selectStyle}
            value={mode}
            onChange={(e) => setMode(e.target.value as DvMode)}
            disabled={saving}
          >
            <option value="disabled">disabled</option>
            <option value="shadow">shadow</option>
            <option value="enabled">enabled</option>
          </select>
        </div>

        <div style={{ marginBottom: 'var(--loop-space-200)', display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)' }}>
          <input
            type="checkbox"
            id="ror-rerunOnPush"
            checked={rerunOnPush}
            onChange={(e) => setRerunOnPush(e.target.checked)}
            disabled={saving}
          />
          <label htmlFor="ror-rerunOnPush" style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-900)', cursor: 'pointer' }}>
            Rerun on push
          </label>
        </div>

        {error && (
          <div style={{ color: 'var(--loop-red-700)', fontSize: '0.8rem', marginBottom: 'var(--loop-space-150)', padding: 'var(--loop-space-100)', background: 'var(--loop-red-100)', borderRadius: 'var(--loop-radius-xs)' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--loop-space-100)', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => { void handleSave(); }} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
